import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { Building2, Plus, Trash2, Copy, RefreshCcw, Pencil, ExternalLink } from "lucide-react";
import { ImageUpload } from "../../components/ImageUpload";
import { mediaUrl } from "../../media";

const EMPTY = { name:"", segment:"Estética", contact_name:"", contact_email:"", phone:"", notes:"", image_url:"" };

export default function BusinessPartnersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => { api.get("/admin/business-partners").then(r=>setItems(r.data)); };
  useEffect(load, []);

  const save = async () => {
    if (!form.name) return toast.error("Nome obrigatório");
    try {
      if (editingId) {
        await api.patch(`/admin/business-partners/${editingId}`, form);
        toast.success("Empresa atualizada");
      } else {
        await api.post("/admin/business-partners", form);
        toast.success("Empresa parceira cadastrada");
      }
      setForm(EMPTY); setEditingId(null); load();
    } catch { toast.error("Falha ao salvar"); }
  };
  const del = async (id) => { if (!window.confirm("Excluir empresa parceira?")) return; await api.delete(`/admin/business-partners/${id}`); load(); };
  const regen = async (id) => { await api.post(`/admin/business-partners/${id}/regenerate-token`); toast.success("Novo link gerado"); load(); };
  const startEdit = (p) => { setEditingId(p.business_partner_id); setForm({...p}); };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const portalUrl = (t) => `${window.location.origin}/parceiro/${t}`;
  const copyLink = (t) => { navigator.clipboard.writeText(portalUrl(t)); toast.success("Link copiado!"); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {items.map(p => (
          <div key={p.business_partner_id} className="card-arya p-5" data-testid={`bp-${p.business_partner_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              {p.image_url && <img src={mediaUrl(p.image_url)} alt={p.name} className="w-20 h-20 rounded-xl object-cover" data-testid={`bp-img-${p.business_partner_id}`}/>}
              <div className="flex-1">
                <span className="tag-sun">{p.segment}</span>
                <h3 className="font-serif text-2xl mt-2">{p.name}</h3>
                <p className="text-xs text-[color:var(--ink-500)] mt-1">
                  {p.contact_name || "—"} · {p.contact_email || "—"} · {p.phone || "—"}
                </p>
                {p.notes && <p className="text-sm text-[color:var(--ink-500)] mt-2">{p.notes}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={()=>startEdit(p)} className="p-2 border border-[var(--line)] rounded-full text-[color:var(--sun-700)]" data-testid={`edit-bp-${p.business_partner_id}`}><Pencil size={14}/></button>
                <button onClick={()=>del(p.business_partner_id)} className="p-2 text-[color:var(--danger)]"><Trash2 size={14}/></button>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-dashed border-[color:var(--sun-500)] p-3 bg-[rgba(233,204,133,.08)]">
              <p className="text-[10px] tracking-widest font-display text-[color:var(--sun-700)] mb-1">LINK DE ACESSO DA EMPRESA (portal restrito)</p>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="font-mono text-xs text-[color:var(--ink-700)] break-all flex-1">{portalUrl(p.access_token)}</code>
                <button onClick={()=>copyLink(p.access_token)} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid={`copy-link-${p.business_partner_id}`}><Copy size={12}/> Copiar</button>
                <a href={portalUrl(p.access_token)} target="_blank" rel="noreferrer" className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1"><ExternalLink size={12}/> Abrir</a>
                <button onClick={()=>regen(p.business_partner_id)} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid={`regen-${p.business_partner_id}`}><RefreshCcw size={12}/> Novo link</button>
              </div>
            </div>
          </div>
        ))}
        {items.length===0 && <div className="card-arya p-8 text-center text-[color:var(--ink-500)]">Nenhuma empresa parceira cadastrada.</div>}
      </div>

      <div className="card-arya p-6 h-fit">
        <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Building2 size={16}/> {editingId ? "Editar empresa" : "Nova empresa parceira"}</h3>
        <div className="space-y-2">
          <input placeholder="Nome da empresa" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="bp-name"/>
          <input placeholder="Ramo (estética, saúde, moda...)" value={form.segment} onChange={e=>setForm({...form, segment:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="bp-segment"/>
          <input placeholder="Contato (pessoa)" value={form.contact_name} onChange={e=>setForm({...form, contact_name:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <input placeholder="Email" value={form.contact_email} onChange={e=>setForm({...form, contact_email:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <input placeholder="Telefone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <textarea rows="2" placeholder="Notas / observação" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}
                    className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <ImageUpload value={form.image_url} onChange={(u)=>setForm({...form, image_url:u})} label="Foto da empresa (aparece no site)" testid="bp-image"/>
          <div className="flex gap-2">
            <button onClick={save} className="btn-gold flex-1 inline-flex items-center justify-center gap-1" data-testid="btn-save-bp"><Plus size={14}/> {editingId ? "Salvar" : "Cadastrar"}</button>
            {editingId && <button onClick={cancelEdit} className="btn-ghost">Cancelar</button>}
          </div>
          <p className="text-[10px] text-[color:var(--ink-500)] leading-relaxed pt-2">
            Ao cadastrar, um <b>link único e restrito</b> é gerado para essa empresa. Ela poderá acompanhar apenas os clientes que forem vinculados a ela — sem qualquer acesso ao painel administrativo, finanças, equipe ou dados privados.
          </p>
        </div>
      </div>
    </div>
  );
}
